import isEmpty from "lodash/isEmpty";
import merge from "lodash/merge";
import pick from "lodash/pick";
import { getRowFromDataItem } from "PFApp/booking/parts/helpers";
import { ChildrenType } from "PFApp/booking/parts/overview/side_panel_row/get_request_data";
import { Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { RowType } from "PFApp/booking/types/row";
import { isProjects, isWorkforceProfiles } from "PFApp/booking/utils/calendar_root.utils";
import { Template } from "PFCore/types";

import { ChildrenDataByRowKey } from "../children_rows";

const getChildrenForRowType = (
  calendarRoot: CalendarRoot,
  row: Row,
  childrenDataByRowKey: ChildrenDataByRowKey
) => {
  const childrenByChildrenType = childrenDataByRowKey[row.key];
  if (!childrenByChildrenType) {
    return undefined;
  }

  if (isWorkforceProfiles(calendarRoot)) {
    switch (row.type) {
      case RowType.Profile:
        return childrenByChildrenType.activity?.response.entries ?? [];
      default:
        return [];
    }
  }
  if (isProjects(calendarRoot)) {
    switch (row.type) {
      case RowType.Engagement:
        return [
          ...(childrenByChildrenType.profile?.response.entries ?? []),
          ...(childrenByChildrenType.role?.response.entries ?? [])
        ];
      case RowType.Role:
        return childrenByChildrenType.profile?.response.entries ?? [];
      default:
        return [];
    }
  }
  return [];
};

export const getBookingsLevelChildrenMeta = (
  calendarRoot: CalendarRoot,
  row: Row,
  childrenDataByRowKey: ChildrenDataByRowKey
): { total: number; totalPages: number } | undefined => {
  const childrenByChildrenType = childrenDataByRowKey[row.key];
  if (!childrenByChildrenType) {
    return undefined;
  }

  const childrenType = {
    [CalendarRoot.WorkforceProfiles]: "activity",
    [CalendarRoot.ProjectsEngagements]: "profile",
    [CalendarRoot.ProjectsRoles]: "profile"
  }[calendarRoot];

  const childrenMeta = childrenByChildrenType[childrenType]?.response.meta;
  if (!childrenType || !childrenMeta) {
    return undefined;
  }

  return pick(childrenMeta, "total", "totalPages");
};

const getPaginatedChildrenMetaByChildrenType = (
  row: Row,
  childrenDataByRowKey: ChildrenDataByRowKey
): Partial<Record<ChildrenType, { total: number; totalPages: number }>> => {
  const childrenByChildrenType = childrenDataByRowKey[row.key];
  if (!childrenByChildrenType) {
    return {};
  }

  const result: Partial<Record<ChildrenType, { total: number; totalPages: number }>> = {};
  for (const [childrenType, data] of Object.entries(childrenByChildrenType)) {
    if (data?.response.meta) {
      result[childrenType] = pick(data.response.meta, "total", "totalPages");
    }
  }
  return result;
};

const getChildrenRows = (
  row: Row,
  calendarRoot: CalendarRoot,
  childrenDataByRowKey: ChildrenDataByRowKey,
  templates: Template[]
) => {
  const childrenEntries = getChildrenForRowType(calendarRoot, row, childrenDataByRowKey);
  return (
    childrenEntries?.map((entry) =>
      getRowFromDataItem(entry, templates, calendarRoot, [...row.parents, row.key])
    ) ?? []
  );
};

type AppendChildrenToRows = {
  rootRows: Row[];
  calendarRoot: CalendarRoot;
  childrenDataByRowKey: ChildrenDataByRowKey;
  templates: Template[];
  isRoleLevelHiddenForEngagementsRoot: boolean;
};
type AppendChildrenToRowsReturn = {
  rowsWithChildren: Row[];
  childrenMetaByParentRowKey: Record<
    string,
    Partial<Record<ChildrenType, { total: number; totalPages: number }>>
  >;
};

export const appendChildrenToRows = ({
  rootRows,
  calendarRoot,
  childrenDataByRowKey,
  templates,
  isRoleLevelHiddenForEngagementsRoot
}: AppendChildrenToRows): AppendChildrenToRowsReturn => {
  if (isEmpty(rootRows)) {
    return { rowsWithChildren: [], childrenMetaByParentRowKey: {} };
  }
  const childrenMetaByParentRowKey = {};
  const rowsWithChildren = rootRows.reduce((result, currentRow) => {
    const childrenRows = getChildrenRows(currentRow, calendarRoot, childrenDataByRowKey, templates);
    // recursive appending children rows (childrenRows become rootRows in the next iteration)
    const {
      rowsWithChildren: childrenRowsWithNestedChildren,
      childrenMetaByParentRowKey: nestedLevelChildrenMetaByKey
    } = appendChildrenToRows({
      rootRows: childrenRows,
      calendarRoot,
      childrenDataByRowKey,
      templates,
      isRoleLevelHiddenForEngagementsRoot
    });

    const metaByChildrenType = getPaginatedChildrenMetaByChildrenType(currentRow, childrenDataByRowKey);
    merge(
      childrenMetaByParentRowKey,
      nestedLevelChildrenMetaByKey,
      Object.keys(metaByChildrenType).length > 0 ? { [currentRow.key]: metaByChildrenType } : {}
    );

    return [...result, currentRow, ...childrenRowsWithNestedChildren];
  }, [] as Row[]);

  return { rowsWithChildren, childrenMetaByParentRowKey };
};
